import { useRef } from "react";

import { useMutation } from "@tanstack/react-query";

import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { createIdempotencyKey } from "../../lib/idempotency";
import { useToast } from "../../ui";

export function useTranscriptRerun(transcriptId: string) {
  const toast = useToast();
  const retryKey = useRef<{ key: string; signature: string } | null>(null);

  return useMutation({
    mutationFn: async (markerIds: string[]) => {
      const signature = JSON.stringify([...markerIds].sort());
      const current =
        retryKey.current?.signature === signature
          ? retryKey.current
          : { key: createIdempotencyKey(), signature };
      retryKey.current = current;
      const { data, error } = await apiClient.POST("/v1/transcripts:evaluate", {
        body: {
          marker_ids: [...markerIds].sort(),
          launch_key: current.key,
          labels_as_of: null,
          selection: null,
          marker_revisions: null,
          transcript_ids: [transcriptId],
        },
        params: { header: { "Idempotency-Key": current.key } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: (result) => {
      retryKey.current = null;
      if (result.enqueued === 0) {
        toast.error("No marker re-runs queued", {
          description:
            `${String(result.skipped)} inapplicable; ` +
            `${String(result.skipped_duplicate_targets)} already queued.`,
        });
      } else {
        toast.success("Marker re-run queued");
      }
    },
    onError: (error: Error) => {
      toast.error("Could not queue marker re-run", {
        description: error.message,
      });
    },
  });
}
